import type { FormTreeQuestion } from '../../../../pages/formSchema/formSchemaTypes';
import {
  buildQuestionTreeHierarchy,
  type QuestionTreeParentInfo,
} from '../../walkQuestionTree';
import { assertLayoutNodeExhaustive } from './layoutAssertNever';
import { walkLayoutStructureChildren } from './layoutStructureChildLists';
import type { ConditionalWhenClause, LayoutNode } from './types';
import { resolveConditionalClauses } from './whenClause';

export type LayoutSchemaGateIssue =
  | {
      qid: string;
      kind: 'top-level-gated';
      gateType: 'conditional' | 'disabled-when';
    }
  | {
      qid: string;
      kind: 'non-ancestor-gate';
      gateQid: string;
      gateType: 'conditional' | 'disabled-when';
    }
  | {
      qid: string;
      kind: 'wrong-ancestor-answer';
      gateQid: string;
      gateAnswer: string;
      /** Sorted schema answer key(s) for this ancestor (one, or several when multi-homed). */
      expectedAnswer: string;
      gateType: 'conditional' | 'disabled-when';
      /** True when the clause was `{ qid, in: [...] }` (shapes the summary wording). */
      gateInList?: true;
    }
  | {
      qid: string;
      kind: 'multi-home-requires-in';
      gateQid: string;
      gateAnswer: string;
      /** Sorted schema answer keys the field is multi-homed under. */
      expectedAnswer: string;
      gateType: 'conditional' | 'disabled-when';
    };

type GateType = 'conditional' | 'disabled-when';

type GateFrame = {
  gateType: GateType;
  clauses: ConditionalWhenClause[];
};

/** ancestor QID → schema subitems answer key(s) linking that ancestor to the field. */
type AncestorAnswerPath = Map<string, string[]>;

/** Parsed `{ qid, eq | includes | in }` clause used for schema-path validation. */
type SchemaAncestorClause = {
  gateQid: string;
  issueKeySuffix: string;
  displayValue: string;
  isInList: boolean;
  matchesSchemaPath: (expectedAnswers: string[]) => boolean;
};

/** Stable display for one or many schema answer keys. */
export function formatExpectedAncestorAnswers(
  expectedAnswers: string[]
): string {
  if (expectedAnswers.length === 0) return '';
  if (expectedAnswers.length === 1) return expectedAnswers[0]!;
  return expectedAnswers.join(' | ');
}

function parseSchemaAncestorClause(
  clause: ConditionalWhenClause
): SchemaAncestorClause | null {
  if (!('qid' in clause)) return null;

  if ('eq' in clause) {
    return {
      gateQid: clause.qid,
      issueKeySuffix: clause.eq,
      displayValue: clause.eq,
      isInList: false,
      matchesSchemaPath: expectedAnswers => expectedAnswers.includes(clause.eq),
    };
  }

  if ('includes' in clause) {
    return {
      gateQid: clause.qid,
      issueKeySuffix: clause.includes,
      displayValue: clause.includes,
      isInList: false,
      matchesSchemaPath: expectedAnswers =>
        expectedAnswers.includes(clause.includes),
    };
  }

  if ('in' in clause) {
    const displayValue =
      clause.in.length === 0 ? '[]' : `[${clause.in.join(', ')}]`;
    return {
      gateQid: clause.qid,
      issueKeySuffix: `in:${[...clause.in].sort().join(',')}`,
      displayValue,
      isInList: true,
      // Must cover every schema home (one gate for multi-homed children).
      matchesSchemaPath: expectedAnswers =>
        expectedAnswers.every(key => clause.in.includes(key)),
    };
  }

  return null;
}

function buildAncestorAnswerPaths(
  parentOf: Map<string, QuestionTreeParentInfo>
): Map<string, AncestorAnswerPath> {
  const paths = new Map<string, AncestorAnswerPath>();

  const collect = (qid: string): AncestorAnswerPath => {
    const cached = paths.get(qid);
    if (cached) return cached;

    const path: AncestorAnswerPath = new Map();
    const parent = parentOf.get(qid);
    if (parent != null) {
      path.set(parent.parentQid, [...parent.answerKeys]);
      for (const [ancestorQid, answerKeys] of collect(parent.parentQid)) {
        path.set(ancestorQid, answerKeys);
      }
    }
    paths.set(qid, path);
    return path;
  };

  for (const qid of parentOf.keys()) {
    collect(qid);
  }
  return paths;
}

function validateSchemaGateClause(
  fieldQid: string,
  frame: GateFrame,
  clause: ConditionalWhenClause,
  ancestorPath: AncestorAnswerPath,
  seen: Set<string>
): LayoutSchemaGateIssue | null {
  const parsed = parseSchemaAncestorClause(clause);
  if (parsed == null) return null;

  const dedupeKey = `${frame.gateType}:${parsed.gateQid}:${parsed.issueKeySuffix}`;
  if (seen.has(dedupeKey)) return null;

  const expectedAnswers = ancestorPath.get(parsed.gateQid);
  if (expectedAnswers == null) {
    seen.add(dedupeKey);
    return {
      qid: fieldQid,
      kind: 'non-ancestor-gate',
      gateQid: parsed.gateQid,
      gateType: frame.gateType,
    };
  }

  // Multi-homed under several answers of this parent: require one `in` gate
  // covering all homes — do not copy-paste the field under per-answer
  // `eq` / `includes` branches.
  if (expectedAnswers.length > 1 && !parsed.isInList) {
    seen.add(dedupeKey);
    return {
      qid: fieldQid,
      kind: 'multi-home-requires-in',
      gateQid: parsed.gateQid,
      gateAnswer: parsed.displayValue,
      expectedAnswer: formatExpectedAncestorAnswers(expectedAnswers),
      gateType: frame.gateType,
    };
  }

  if (parsed.matchesSchemaPath(expectedAnswers)) return null;

  seen.add(dedupeKey);
  return {
    qid: fieldQid,
    kind: 'wrong-ancestor-answer',
    gateQid: parsed.gateQid,
    gateAnswer: parsed.displayValue,
    expectedAnswer: formatExpectedAncestorAnswers(expectedAnswers),
    gateType: frame.gateType,
    ...(parsed.isInList ? { gateInList: true as const } : {}),
  };
}

function recordFieldQid(
  qid: string,
  gateStack: GateFrame[],
  hierarchy: ReturnType<typeof buildQuestionTreeHierarchy>,
  ancestorPaths: Map<string, AncestorAnswerPath>,
  issues: LayoutSchemaGateIssue[]
) {
  if (gateStack.length === 0) return;

  const innermostGateType = gateStack[gateStack.length - 1]!.gateType;

  if (hierarchy.topLevelQids.has(qid)) {
    issues.push({ qid, kind: 'top-level-gated', gateType: innermostGateType });
    return;
  }

  const ancestorPath = ancestorPaths.get(qid);
  if (ancestorPath == null) return;

  const seen = new Set<string>();
  for (const frame of gateStack) {
    for (const clause of frame.clauses) {
      const issue = validateSchemaGateClause(
        qid,
        frame,
        clause,
        ancestorPath,
        seen
      );
      if (issue != null) issues.push(issue);
    }
  }
}

function issueSortKey(issue: LayoutSchemaGateIssue): string {
  switch (issue.kind) {
    case 'top-level-gated':
      return `0:${issue.qid}`;
    case 'non-ancestor-gate':
      return `1:${issue.qid}:${issue.gateQid}:${issue.gateType}`;
    case 'multi-home-requires-in':
      return `2:${issue.qid}:${issue.gateQid}:${issue.gateAnswer}:${issue.gateType}`;
    case 'wrong-ancestor-answer':
      return `3:${issue.qid}:${issue.gateQid}:${issue.gateAnswer}:${issue.gateType}`;
  }
}

/**
 * Flags layout fields that violate schema parent/child gating rules:
 * - top-level schema QIDs must not sit inside `conditional` or `disabled-when`
 * - nested subitem QIDs inside a gate may reference schema ancestors on
 *   `{ qid, eq }` / `{ qid, includes }` / `{ qid, in }` clauses, but never
 *   non-ancestors
 * - single-home: `{ qid, eq }` / `{ qid, includes }` must use that ancestor's
 *   schema subitems answer key; `{ qid, in }` must include it
 * - multi-home (same child under several keys of one parent): do not gate with
 *   per-answer `eq`/`includes` (no copy-pasted branches); use one `{ qid, in }`
 *   that lists every schema home
 * - gating nested subitems is optional (no gate is valid)
 */
export function collectLayoutSchemaGateIssues(
  nodes: LayoutNode[],
  questions: FormTreeQuestion[]
): LayoutSchemaGateIssue[] {
  const hierarchy = buildQuestionTreeHierarchy(questions);
  const ancestorPaths = buildAncestorAnswerPaths(hierarchy.parentOf);
  const issues: LayoutSchemaGateIssue[] = [];

  const walk = (arr: LayoutNode[], gateStack: GateFrame[]) => {
    for (const node of arr) {
      switch (node.type) {
        case 'conditional': {
          const frame: GateFrame = {
            gateType: 'conditional',
            clauses: resolveConditionalClauses(node),
          };
          const nextStack = [...gateStack, frame];
          walk(node.children, nextStack);
          if (node.elseChildren) walk(node.elseChildren, nextStack);
          break;
        }
        case 'disabled-when': {
          const frame: GateFrame = {
            gateType: 'disabled-when',
            clauses: node.whenAll,
          };
          walk(node.children, [...gateStack, frame]);
          break;
        }
        case 'table': {
          // A row gates visibility with the same clause shape as `conditional`,
          // so push a per-row frame (when the row is actually gated) before
          // walking its cells. Ungated rows keep the inherited gate stack.
          for (const row of node.rows) {
            const clauses = resolveConditionalClauses(row);
            const nextStack =
              clauses.length > 0
                ? [...gateStack, { gateType: 'conditional' as const, clauses }]
                : gateStack;
            walk(row.cells, nextStack);
          }
          break;
        }
        case 'OASISRadio':
        case 'OASISYesNoCheckbox':
        case 'OASISSelect':
        case 'OASISText':
        case 'OASISDate':
        case 'OASISTime':
        case 'OASISNumber':
        case 'radio-option':
          recordFieldQid(node.qid, gateStack, hierarchy, ancestorPaths, issues);
          break;
        case 'OASISMultiselect':
          recordFieldQid(node.qid, gateStack, hierarchy, ancestorPaths, issues);
          walkLayoutStructureChildren(node, childList =>
            walk(childList, gateStack)
          );
          break;
        case 'card':
        case 'stack':
        case 'grid':
        case 'flex':
        case 'box':
        case 'indent':
        case 'field-row':
        case 'divider':
        case 'static':
        case 'text':
        case 'display':
        case 'section-label':
        case 'sum':
        case 'repeat':
        case 'OASISMultiselectOption':
          walkLayoutStructureChildren(node, childList =>
            walk(childList, gateStack)
          );
          break;
        default:
          assertLayoutNodeExhaustive(node);
      }
    }
  };

  walk(nodes, []);
  return issues.sort((a, b) => issueSortKey(a).localeCompare(issueSortKey(b)));
}

function formatWrongAncestorAnswerIssue(
  issue: Extract<LayoutSchemaGateIssue, { kind: 'wrong-ancestor-answer' }>
): string {
  const isInList = issue.gateInList === true;
  const multi = issue.expectedAnswer.includes(' | ');
  const expectedPhrase = multi
    ? `one of ${issue.gateQid} = ${issue.expectedAnswer}`
    : `${issue.gateQid} = "${issue.expectedAnswer}"`;
  if (issue.gateType === 'disabled-when') {
    if (isInList) {
      return `${issue.qid} (disabled-when whenAll must keep field enabled on ${expectedPhrase}, but in clause ${issue.gateAnswer} does not include every home)`;
    }
    return `${issue.qid} (disabled-when whenAll must keep field enabled on ${expectedPhrase}, not "${issue.gateAnswer}")`;
  }
  if (isInList) {
    return `${issue.qid} (conditional when must match ${expectedPhrase}, but in clause ${issue.gateAnswer} does not include every home)`;
  }
  return `${issue.qid} (conditional when must match ${expectedPhrase}, not "${issue.gateAnswer}")`;
}

export function formatLayoutSchemaGateIssueSummary(
  issue: LayoutSchemaGateIssue
): string {
  switch (issue.kind) {
    case 'top-level-gated':
      return `${issue.qid} (top-level schema question inside ${issue.gateType})`;
    case 'non-ancestor-gate':
      return `${issue.qid} (gate references non-ancestor ${issue.gateQid} inside ${issue.gateType})`;
    case 'multi-home-requires-in':
      return `${issue.qid} (multi-homed under ${issue.gateQid} = ${issue.expectedAnswer}; use one {in:[...]} covering all homes, not per-answer "${issue.gateAnswer}")`;
    case 'wrong-ancestor-answer':
      return formatWrongAncestorAnswerIssue(issue);
  }
}
